import { useState, useMemo } from 'react';
import { Loader2, Headphones, Plus, AlertTriangle, X, SlidersHorizontal, Search } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { QueueTabs, type QueueTabKey } from './QueueTabs';
import { SessionCard } from './SessionCard';
import { MissedCallsPanel } from './MissedCallsPanel';
import { NewSessionModal } from '@/components/customer-service/NewSessionModal';
import { useSupportInbox, DEMAND_TYPE_CONFIG } from '@/hooks/useSupportInbox';
import { useOpenEscalationSessions } from '@/hooks/useOpenEscalationSessions';
import { useDebounce } from '@/hooks/useDebounce';
import type { SessionWithSla, SessionFilters as SessionFiltersType, DemandType } from '@/types/support.types';

interface SessionListProps {
  onSelectSession: (session: SessionWithSla) => void;
  selectedSessionId: string | null;
}

export function SessionList({ onSelectSession, selectedSessionId }: SessionListProps) {
  const [activeQueue, setActiveQueue] = useState<QueueTabKey>('queue_respond');
  const [newSessionOpen, setNewSessionOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [demandTypeFilter, setDemandTypeFilter] = useState<string | null>(null);
  const [agentFilter, setAgentFilter] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const debouncedSearch = useDebounce(search, 300);

  // Fetch ALL non-resolved sessions to get counts per queue
  const { sessions: allSessions, isLoading, stats } = useSupportInbox({
    search: debouncedSearch,
  });

  // Sessões com escalação interna pendente (badge no card)
  const openEscalationSessions = useOpenEscalationSessions();



  // Sessions sem pre_ticket — usadas nas abas Interno e Cliente
  const nonPreTicketSessions = useMemo(
    () => allSessions.filter(s => s.status !== 'pre_ticket'),
    [allSessions]
  );

  // Extract unique agents from sessions
  const agents = useMemo(() => {
    const agentMap = new Map<string, string>();
    allSessions.forEach(s => {
      if (s.assigned_to && s.agent_name) {
        agentMap.set(s.assigned_to, s.agent_name);
      }
    });
    return Array.from(agentMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [allSessions]);

  // Compute counts per queue
  // queue_respond inclui pré-chamados; Interno e Cliente não têm pré-chamados
  const queueCounts = useMemo(() => ({
    queue_respond: allSessions.filter(s => s.computed_queue === 'queue_respond').length,
    queue_internal: nonPreTicketSessions.filter(s => s.computed_queue === 'queue_internal').length,
    queue_client: nonPreTicketSessions.filter(s => s.computed_queue === 'queue_client').length,
  }), [allSessions, nonPreTicketSessions]);

  const hasActiveFilters = !!demandTypeFilter || !!agentFilter;

  // Sessões base filtradas pela fila ativa
  const baseFiltered = useMemo(() => {
    // Para Interno e Cliente, nunca mostrar pré-chamados
    const source = activeQueue === 'queue_respond' ? allSessions : nonPreTicketSessions;
    let filtered = source.filter(s => s.computed_queue === activeQueue);

    if (demandTypeFilter) {
      filtered = filtered.filter(s => s.demand_type === demandTypeFilter);
    }
    if (agentFilter === 'unassigned') {
      filtered = filtered.filter(s => !s.assigned_to);
    } else if (agentFilter) {
      filtered = filtered.filter(s => s.assigned_to === agentFilter);
    }

    return filtered;
  }, [allSessions, nonPreTicketSessions, activeQueue, demandTypeFilter, agentFilter]);

  // Todas as sessões juntas, ordenadas por updated_at DESC (última atividade no topo)
  const sortedSessions = useMemo(() => {
    return [...baseFiltered].sort((a, b) => {
      const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return bTime - aTime;
    });
  }, [baseFiltered]);

  const totalVisible = sortedSessions.length;

  return (
    <div className="flex flex-col h-full bg-wa-sidebar border-r border-wa-divider">
      {/* Header */}
      <div className="p-3 border-b border-wa-divider space-y-3 bg-wa-sidebar">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-lg font-semibold text-wa-text">Suporte</h1>
          <div className="flex items-center gap-2">
            {stats.breached_sla > 0 && (
              <span className="text-xs font-medium text-destructive bg-destructive/10 px-2 py-1 rounded-full flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {stats.breached_sla} SLA
              </span>
            )}
            <MissedCallsPanel />


            <Button
              variant="ghost"
              size="sm"
              onClick={() => setNewSessionOpen(true)}
              className="rounded-full h-9 w-9 p-0 flex-shrink-0 hover:bg-wa-sidebar-active text-wa-text-muted"
              title="Novo chamado"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Queue Tabs */}
        <QueueTabs
          activeQueue={activeQueue}
          onQueueChange={setActiveQueue}
          counts={queueCounts}
        />

        {/* Search + Filter toggle */}
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-wa-text-muted h-4 w-4" />
            <Input
              placeholder="Pesquisar"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full h-9 bg-wa-sidebar-active border-0 rounded-lg text-[14px] focus-visible:ring-1 focus-visible:ring-wa-green"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={cn(
              'rounded-full h-9 w-9 p-0 flex-shrink-0',
              hasActiveFilters
                ? 'bg-wa-green text-white hover:bg-wa-green/90'
                : 'hover:bg-wa-sidebar-active text-wa-text-muted'
            )}
            title="Filtros"
          >
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
        </div>

        {/* Collapsible Filters */}
        {filtersOpen && (
          <div className="flex items-center gap-2">
            <Select
              value={demandTypeFilter || 'all'}
              onValueChange={(v) => setDemandTypeFilter(v === 'all' ? null : v)}
            >
              <SelectTrigger className="h-8 text-xs flex-1">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Tipos</SelectItem>
                {Object.entries(DEMAND_TYPE_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={agentFilter || 'all'}
              onValueChange={(v) => setAgentFilter(v === 'all' ? null : v)}
            >
              <SelectTrigger className="h-8 text-xs flex-1">
                <SelectValue placeholder="Atendente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Atendentes</SelectItem>
                <SelectItem value="unassigned">Sem atendente</SelectItem>
                {agents.map(([id, name]) => (
                  <SelectItem key={id} value={id}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => { setDemandTypeFilter(null); setAgentFilter(null); }}
                title="Limpar filtros"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Session List */}
      <ScrollArea className="flex-1 bg-wa-sidebar">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : totalVisible === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
            <Headphones className="h-10 w-10 mb-2 opacity-30" />
            <p className="text-sm">Nenhum chamado nesta fila</p>
          </div>
        ) : (
          <div>
            {sortedSessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                isSelected={selectedSessionId === session.id}
                hasOpenEscalation={openEscalationSessions.has(session.id)}
                onClick={() => onSelectSession(session)}
              />
            ))}
          </div>
        )}
      </ScrollArea>

      <NewSessionModal
        open={newSessionOpen}
        onOpenChange={setNewSessionOpen}
        onSessionCreated={() => {}}
      />
    </div>
  );
}
